// Video Render API Functions using AppSync/GraphQL
import {
  START_VIDEO_RENDER_MUTATION,
  GET_RENDER_PROGRESS_QUERY,
  GET_RENDER_DOWNLOAD_URL_QUERY,
} from "../queries/renderQueries";
import { ApolloClient, InMemoryCache, createHttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { getAuthToken, clearAuthState } from "../utils/authUtils";

// Use the Library Card API endpoint (or create a separate one for renders)
const GRAPHQL_ENDPOINT =
  "https://m2jxbyhagffjthxtsiuwb2iiwe.appsync-api.us-east-1.amazonaws.com/graphql";

const httpLink = createHttpLink({
  uri: GRAPHQL_ENDPOINT,
  // Add fetch options to ensure requests are visible
  fetchOptions: {
    credentials: "omit",
  },
  // Override fetch to add logging and ensure requests are visible
  fetch: (uri, options) => {
    console.log("🌐 renderApi - Making GraphQL request:", {
      uri,
      method: options?.method || "POST",
      headers: options?.headers,
      hasBody: !!options?.body,
      bodyLength: options?.body ? options.body.length : 0,
    });
    console.log("🌐 renderApi - This request SHOULD appear in Network tab!");
    console.log("🌐 renderApi - Look for:", uri);
    // Use native fetch to ensure it shows in Network tab
    return fetch(uri, {
      ...options,
      // Ensure it's not cached
      cache: "no-cache",
    });
  },
});

const authLink = setContext((_, { headers }) => {
  const token = getAuthToken();
  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: token } : {}),
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, extensions }) => {
      if (
        extensions?.errorType === "UnauthorizedException" ||
        message.includes("Unauthorized")
      ) {
        console.error("renderApi - Unauthorized error detected");
        clearAuthState();
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    });
  }

  if (networkError?.statusCode === 401) {
    console.error("renderApi - 401 Unauthorized network error");
    clearAuthState();
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
  }
});

const apolloClient = new ApolloClient({
  link: errorLink.concat(authLink).concat(httpLink),
  cache: new InMemoryCache(),
  // Disable cache for render operations to ensure requests are always visible
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "network-only",
    },
    query: {
      fetchPolicy: "network-only",
    },
  },
});

/**
 * Start a video render job
 * @param {Object} params - { id: string, inputProps: CompositionProps, baseUrl?: string, renderType?: string }
 * @returns {Promise<Object>} - { renderId: string, bucketName?: string }
 */
export const startVideoRender = async (params) => {
  try {
    console.log("🌐 API: startVideoRender CALLED!", {
      timestamp: new Date().toISOString(),
      id: params.id,
      inputPropsKeys: params.inputProps ? Object.keys(params.inputProps) : null,
      inputPropsSize: params.inputProps
        ? JSON.stringify(params.inputProps).length
        : 0,
      baseUrl: params.baseUrl,
      renderType: params.renderType,
    });

    // Convert inputProps to JSON string for AWSJSON! type
    const inputPropsJson =
      typeof params.inputProps === "string"
        ? params.inputProps
        : JSON.stringify(params.inputProps);

    // Get baseUrl from params or window location
    const baseUrl =
      params.baseUrl ||
      (typeof window !== "undefined" ? window.location.origin : undefined);

    // Get renderType from params (default: "lambda")
    const renderType = params.renderType || "lambda";

    // Composition ID must match Remotion bundle (e.g. "PiperVideoEditor"). Never send empty.
    const compositionId =
      (params.id && String(params.id).trim()) || "PiperVideoEditor";

    console.log("🌐 API: Preparing GraphQL mutation:", {
      mutation: "startVideoRender",
      mutationName:
        START_VIDEO_RENDER_MUTATION.definitions[0]?.name?.value ||
        "StartVideoRender",
      compositionId,
      inputPropsLength: inputPropsJson.length,
      inputPropsPreview: inputPropsJson.substring(0, 200) + "...",
      baseUrl,
      renderType,
      endpoint: GRAPHQL_ENDPOINT,
      apolloClientConfigured: !!apolloClient,
    });

    // Log the exact mutation variables being sent
    // Always include renderType (even though optional in schema) to explicitly use Remotion Lambda
    const mutationVariables = {
      input: {
        compositionId,
        inputProps: inputPropsJson,
        renderType: renderType, // Always include renderType (defaults to "lambda")
        ...(baseUrl && { baseUrl }),
      },
    };
    console.log("🌐 API: Mutation variables prepared:", {
      ...mutationVariables,
      input: {
        ...mutationVariables.input,
        inputProps: `[${inputPropsJson.length} chars]`,
      },
    });

    console.log(
      "🌐 API: Calling apolloClient.mutate() - THIS WILL MAKE NETWORK REQUEST..."
    );
    const response = await apolloClient.mutate({
      mutation: START_VIDEO_RENDER_MUTATION,
      variables: mutationVariables,
    });
    console.log(
      "✅ API: apolloClient.mutate() completed - Network request finished"
    );

    console.log("🌐 API: GraphQL mutation response received:", {
      hasData: !!response.data,
      hasErrors: !!response.errors,
      responseKeys: response.data ? Object.keys(response.data) : null,
      timestamp: new Date().toISOString(),
    });

    if (response.errors) {
      console.error("❌ API: GraphQL errors in response:", response.errors);
      throw new Error(response.errors[0]?.message || "Failed to start render");
    }

    const result = response.data?.startVideoRender;
    console.log("✅ API: Mutation successful! Parsed result:", {
      renderId: result?.renderId,
      bucketName: result?.bucketName,
      status: result?.status,
      message: result?.message,
      fullResponse: result,
    });

    const renderId = result?.renderId;
    if (!renderId || typeof renderId !== "string" || !renderId.trim()) {
      console.error("❌ API: startVideoRender returned invalid renderId:", {
        renderId: result?.renderId,
        type: typeof result?.renderId,
      });
      throw new Error(
        "Server did not return a valid render ID. Please try again."
      );
    }

    const returnValue = {
      renderId: renderId.trim(),
      bucketName: result.bucketName,
    };
    console.log("✅ API: startVideoRender returning:", returnValue);
    return returnValue;
  } catch (error) {
    console.error("❌ API: Error in startVideoRender:", error);
    console.error("❌ API: Error details:", {
      message: error?.message,
      stack: error?.stack,
      name: error?.name,
    });
    throw error;
  }
};

/**
 * Check render progress
 * @param {Object} params - { id: string, bucketName?: string }
 * @returns {Promise<Object>} - ProgressResponse: { type: "progress" | "done" | "error", progress?: number, url?: string, size?: number, message?: string }
 */
export const getRenderProgress = async (params) => {
  try {
    const renderId = params?.id != null ? String(params.id).trim() : "";
    if (!renderId) {
      console.error("🌐 renderApi - getRenderProgress called with no renderId");
      return {
        type: "error",
        message: "Cannot get progress: render ID is missing.",
      };
    }
    console.log("🌐 renderApi - Getting render progress:", {
      renderId,
      bucketName: params.bucketName,
    });
    console.log(
      "🌐 renderApi - About to call apolloClient.query() for getRenderProgress"
    );

    const response = await apolloClient.query({
      query: GET_RENDER_PROGRESS_QUERY,
      variables: {
        input: {
          renderId: params.id,
          bucketName: params.bucketName,
        },
      },
      fetchPolicy: "no-cache", // Always get fresh progress
    });

    console.log(
      "🌐 renderApi - apolloClient.query() completed for getRenderProgress"
    );

    if (response.errors) {
      console.error("renderApi - GraphQL errors:", response.errors);
      return {
        type: "error",
        message: response.errors[0]?.message || "Failed to get progress",
      };
    }

    const result = response.data?.getRenderProgress;
    console.log("renderApi - Progress response:", {
      type: result.type,
      progress: result.progress,
      hasUrl: !!result.url,
      hasSize: !!result.size,
      message: result.message,
      renderId: result.renderId,
    });

    return {
      type: result.type,
      progress: result.progress,
      url: result.url,
      size: result.size,
      message: result.message,
    };
  } catch (error) {
    console.error("renderApi - Error getting progress:", {
      error: error.message,
      graphQLErrors: error.graphQLErrors,
      networkError: error.networkError,
    });
    return {
      type: "error",
      message: error.message || "Failed to get render progress",
    };
  }
};

/**
 * Get download URL for rendered video
 * @param {Object} params - { renderId: string }
 * @returns {Promise<Object>} - { downloadUrl: string, expiresIn?: number }
 */
export const getRenderDownloadUrl = async (params) => {
  try {
    console.log("renderApi - Getting render download URL:", {
      renderId: params.renderId,
    });

    const response = await apolloClient.query({
      query: GET_RENDER_DOWNLOAD_URL_QUERY,
      variables: {
        input: {
          renderId: params.renderId,
        },
      },
    });

    if (response.errors) {
      console.error("renderApi - GraphQL errors:", response.errors);
      throw new Error(
        response.errors[0]?.message || "Failed to get download URL"
      );
    }

    const result = response.data?.getRenderDownloadUrl;
    console.log("renderApi - Download URL response:", {
      downloadUrl: result?.downloadUrl ? "present" : "missing",
      expiresIn: result?.expiresIn,
    });

    return {
      downloadUrl: result?.downloadUrl,
      expiresIn: result?.expiresIn,
    };
  } catch (error) {
    console.error("renderApi - Error getting download URL:", error);
    throw error;
  }
};
